'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { SectionHeader } from '@/components/ui';
import { ListingCard } from '@/components/cards';
import Carousel from '@/components/carousel/Carousel';
import { CarouselControls } from '@/components/carousel/CarouselControls';
import { getRecommendedListings as getRecommendedListingsApi, type Listing } from '@repo/api';
import { analyticsDataAttrs } from '@/lib/analytics/data-attrs';
import { urgencyParam } from '@/lib/listings/urgency';

// Minimum number of recommendations required before we replace the verified
// listings section with personalized recommendations. Below this threshold
// (including 0 results and errors) we keep showing the server-rendered
// `<VerifiedListings />` passed in via `children`.
const MIN_DYNAMIC_LISTINGS = 3;

interface DynamicListingsProps {
  /**
   * Fallback content to render while recommendations are loading, and to
   * keep rendering if recommendations come back empty / errored / below the
   * MIN_DYNAMIC_LISTINGS threshold.
   *
   * Typically `<VerifiedListings />` so the homepage shows verified listings
   * server-side immediately, then optionally swaps to personalized
   * recommendations after the client-side fetch resolves.
   */
  children: ReactNode;
}

/**
 * DynamicListings (client-side)
 *
 * Personalized recommendations carousel for the homepage. Fetches
 * `/unit/recommendations/` from the browser so the user's `fb_session`
 * cookie is sent automatically (the API client's `defaultCredentials:
 * 'include'` handles this on the browser side — no server-side cookie
 * forwarding gymnastics needed).
 *
 * Render strategy:
 *   - Initial SSR + first client render: shows `children` (server-rendered
 *     `<VerifiedListings />`). No loading flicker.
 *   - After the recommendations fetch resolves: if we got >= 3 listings,
 *     swap to the personalized carousel. Otherwise keep `children`.
 *   - On fetch error / empty / sub-threshold: keep `children`.
 *
 * Why client-side instead of server-side: the recommendations endpoint
 * requires the user's session cookie to personalize. Forwarding cookies
 * from a Next.js Server Component is possible but added complexity and
 * was breaking edge cases (cookie scope mismatches, build-time static
 * generation). Browser fetch sends cookies natively and just works.
 */
export function DynamicListings({ children }: DynamicListingsProps) {
  const [listings, setListings] = useState<Listing[] | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let cancelled = false;

    getRecommendedListingsApi({ pageSize: 6 })
      .then(response => {
        if (cancelled) return;
        setListings(response?.results ?? []);
      })
      .catch(err => {
        // Log so PostHog / console capture it, but don't surface to the user
        // — falling back to verified listings is the correct UX.
        console.error('[DynamicListings] failed to fetch recommendations:', err);
        if (cancelled) return;
        setHasError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Render the personalized carousel only once we've confirmed enough
  // recommendations came back. Before that, OR if the fetch failed, OR if
  // there weren't enough results, render the fallback (children).
  const showDynamic = !hasError && listings !== null && listings.length >= MIN_DYNAMIC_LISTINGS;

  if (!showDynamic) {
    return <>{children}</>;
  }

  return (
    <div className="dynamic-grid-container">
      <section className="section-container !gap-y-2">
        <div className="col-span-full flex items-center justify-between">
          <SectionHeader title="Recommended for you" subtitleText="Based on your activity" />
          {listings?.length > 3 && (
            <div className="hidden md:block pr-4">
              <CarouselControls carouselId="dynamic-listings" />
            </div>
          )}
        </div>

        <Carousel
          className="col-span-full -mx-4 md:mx-0 md:px-2"
          carouselId="dynamic-listings"
          items={listings.map((listing: Listing) => ({
            id: listing.id,
            content: (
              <ListingCard
                listing={listing}
                isSaved={false}
                showOnlyOneImage
                showNetPrice={listing.isNoFee}
                {...analyticsDataAttrs('home_dynamic_listings', {
                  unitId: listing.slug,
                  ...urgencyParam(listing),
                })}
              />
            ),
          }))}
          padStart={{ mobile: 24, tablet: 8, desktop: 8 }}
          padEnd={{ mobile: 24, tablet: 8, desktop: 8 }}
          perView={{
            mobile: 1,
            tablet: 3,
            desktop: 3,
          }}
          peek={{
            mobile: 24,
            tablet: 0,
            desktop: 0,
          }}
          gap={16}
          ariaLabel="Recommended apartment listings"
        />
      </section>
    </div>
  );
}
